import { forwardRef, useRef, useState } from 'react';
import { Box, Button, Collapse, Stack, Typography } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import LaunchIcon from '@mui/icons-material/Launch';
import FlagIcon from '@mui/icons-material/Flag';
import { Link } from 'react-router-dom';
import BuildIcon from '@mui/icons-material/Build';
import { TABLE_CELL_TYPOGRAPHY_STYLES_INVOICED } from '../styles';
import HierarchyConnectorHorizontal from '../HierarchyConnectorHorizontal';
import AnimatedExpandIcon from '../../../../Shared/Components/AnimatedExpandIcon';
import useHierarchyConnectorVertical from '../hooks/useHierarchyConnectorVertical';
import CollapsibleDataGridWithConnectorLines from '../CollapsibleDataGridWithConnectorLines';
import { formatUtcDate } from '../../../../utils/formatDate';
import { COLORS, DATE_FORMATS } from '../../../../utils/Application_Constants';
import { config as TimesheetReviewConfig } from '../../../Configurations/TimesheetReviewConfig';
import { currencyFormatter, decimalFormatter } from '../../../../utils/helpers';
import { rateTypesEnumApiToDisplay } from '../../../Configurations/processedTimesheetsConfig';

const CHILD_PADDING = 6;

// helpers used by column formatters
const getCellValue = (arg) =>
    arg && typeof arg === 'object' && Object.prototype.hasOwnProperty.call(arg, 'value')
        ? arg.value
        : arg;

const fmtDate = (arg) => formatUtcDate(getCellValue(arg), DATE_FORMATS.WEEKDAY_MD);
const fmtDecimal = (arg) => {
    const n = Number(getCellValue(arg));
    return Number.isFinite(n) ? decimalFormatter(n) : '';
};
const fmtCurrency = (arg) => {
    const n = Number(getCellValue(arg));
    return Number.isFinite(n) ? currencyFormatter(n) : currencyFormatter(0);
};

const fmtRateUnit = (arg) => {
    const v = getCellValue(arg);
    return v == null || v === '' ? '' : rateTypesEnumApiToDisplay(v);
};

const timesheetLineItemColumns = [
    {
        field: 'date',
        headerName: 'Day Worked',
        flex: 1,
        valueFormatter: fmtDate,
        cellClassName: 'bold-text',
    },
    { field: 'rateType', headerName: 'Rate Type', flex: 1 },
    {
        field: 'billTime',
        headerName: 'Bill Time',
        flex: 1,
        sortable: false,
        headerClassName: 'left-border',
        cellClassName: 'left-border',
        valueFormatter: fmtDecimal,
    },
    {
        field: 'billUnit',
        headerName: 'Bill Unit',
        flex: 1,
        sortable: false,
        valueFormatter: fmtRateUnit,
    },
    {
        field: 'billRate',
        headerName: 'Bill Rate',
        flex: 1,
        sortable: false,
        valueFormatter: fmtCurrency,
    },
    {
        field: 'billAmount',
        headerName: 'Bill Amount',
        flex: 1,
        sortable: false,
        valueFormatter: fmtCurrency,
    },
];

const expenseColumns = [
    {
        field: 'isCorrection',
        headerName: '',
        width: 35,
        sortable: false,
        renderCell: (cell) =>
            cell.value === true ? (
                <Tooltip title="This expense has corrections">
                    <BuildIcon
                        sx={{
                            color: COLORS.LT_POMEGRANATE,
                            verticalAlign: 'middle',
                            height: 17,
                            width: 17,
                        }}
                    />
                </Tooltip>
            ) : (
                ''
            ),
    },
    {
        field: 'transactionDate',
        headerName: 'Txn Date',
        flex: 1,
        sortable: true,
        valueFormatter: (value) => formatUtcDate(value),
    },
    {
        field: 'startDate',
        headerName: 'Start Date',
        flex: 1,
        sortable: true,
        headerClassName: 'left-border',
        cellClassName: 'left-border',
        valueFormatter: (value) => formatUtcDate(value),
    },
    {
        field: 'endDate',
        headerName: 'End Date',
        flex: 1,
        sortable: true,
        valueFormatter: (value) => formatUtcDate(value),
    },
    {
        field: 'expenseType',
        headerName: 'Expense Type',
        sortable: false,
        flex: 1,
        headerClassName: 'left-border',
        cellClassName: 'left-border',
    },
    {
        field: 'billAmount',
        headerName: 'Bill Amount',
        sortable: false,
        flex: 1,
        headerClassName: 'left-border',
        cellClassName: 'left-border',
        valueFormatter: fmtCurrency,
    },
    {
        field: 'expenseManagement',
        headerName: '',
        flex: 1,
        minWidth: 190,
        headerAlign: 'right',
        align: 'right',
        sortable: false,
        filterable: false,
        renderCell: (params) => (
            <Button
                variant="text"
                component={Link}
                to={`/expense-redirect/${params.row?.id}`}
                target="_blank"
                size="small"
                startIcon={<LaunchIcon />}
                sx={{ p: 0.5, fontSize: 12, whiteSpace: 'nowrap' }}
                onClick={(e) => e.stopPropagation()}
                disabled={!params.row?.id && !params.row?.expenseId}
            >
                Expense Management
            </Button>
        ),
    },
];

const InvoicingItemContainer = (
    {
        hierarchyConnectorVerticalRef: parentHierarchyConnectorVerticalRef,
        type,
        itemId, // locationId for timesheets; group id for expenses (used for keys/collapse id)
        data, // for timesheets: { id, weekEnding, locationName, lineItems[] }
    },
    ref
) => {
    const connectorParentRef = useRef(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const toggleExpand = () => setIsExpanded((prev) => !prev);

    const {
        mainContainerRef,
        expandIconRef,
        lastChildRef,
        hierarchyConnectorVerticalRef,
        renderConnector,
    } = useHierarchyConnectorVertical();

    // determine subitems by the shape
    const hasSubItems =
        (type === 'timesheet' && (data?.groupedTimesheets?.length ?? 0) > 0) ||
        (type === 'expense' && (data?.items?.length ?? 0) > 0);

    // Optional display fields (safe if missing)
    const totalToBill = Number(data?.totals?.totalToBill ?? 0);
    const totalToPay = Number(data?.totals?.totalToPay ?? 0);
    const weekEndingIso =
        typeof data?.weekEnding === 'string'
            ? data.weekEnding
            : data?.weekEnding?.toISOString?.() ?? '';
    const weekEnding = weekEndingIso.slice(0, 10); // YYYY-MM-DD

    const timesheetId = type === 'timesheet' ? data?.timesheetId ?? null : null;
    const processedTimesheetId = type === 'timesheet' ? data?.processedTimesheetId ?? null : null;
    const clinicianId = type === 'timesheet' ? data?.clinicianId ?? null : null;

    const clinicianTimesheetUrl =
        clinicianId && timesheetId && weekEnding && TimesheetReviewConfig?.getTimeEntryUrl
            ? `${TimesheetReviewConfig.getTimeEntryUrl({
                  id: timesheetId,
                  clinicianId,
                  weekEnding, // "YYYY-MM-DD"
              })}&edit=true`
            : '/timesheets';

    return (
        <Box ref={mainContainerRef} position="relative">
            <Stack
                ref={ref}
                direction="row"
                alignItems="center"
                height={80}
                spacing={1}
                sx={{ cursor: hasSubItems ? 'pointer' : 'default', pr: CHILD_PADDING }}
                onClick={hasSubItems ? toggleExpand : undefined}
                aria-expanded={hasSubItems ? isExpanded : undefined}
                aria-controls={hasSubItems ? `subitem-collapse-${itemId}` : undefined}
                gap="60px"
            >
                <Box
                    ref={connectorParentRef}
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        position: 'relative',
                        marginLeft: !hasSubItems ? '31px' : undefined,
                    }}
                >
                    <HierarchyConnectorHorizontal
                        anchorRef={connectorParentRef}
                        verticalRef={parentHierarchyConnectorVerticalRef}
                    />

                    {hasSubItems && (
                        <AnimatedExpandIcon ref={expandIconRef} isExpanded={isExpanded} />
                    )}

                    {type === 'timesheet' && (
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ width: '100%' }}
                        >
                            {/* Left: Timesheet + locations */}
                            <Stack
                                direction="row"
                                alignItems="center"
                                gap={2}
                                sx={{ pl: CHILD_PADDING / 2 }}
                            >
                                <Stack gap={0.5} sx={{ maxWidth: 208 }}>
                                    <Typography sx={TABLE_CELL_TYPOGRAPHY_STYLES_INVOICED}>
                                        Timesheet
                                    </Typography>
                                    <Typography
                                        sx={{
                                            ...TABLE_CELL_TYPOGRAPHY_STYLES_INVOICED,
                                            fontSize: 12,
                                            fontStyle: 'italic',
                                        }}
                                    >
                                        Locations: {Number(data?.locationsCount ?? 0)}
                                    </Typography>
                                </Stack>
                            </Stack>

                            {/* Right: booking, week ending, totals, links */}
                            <Stack
                                direction="row"
                                alignItems="center"
                                gap={3}
                                sx={{ pl: CHILD_PADDING / 2 }}
                            >
                                {data?.bookingId && data.isHighPriority && (
                                    <Tooltip title="High Priority" placement="top" arrow>
                                        <FlagIcon
                                            sx={{
                                                color: COLORS.LT_PEPPER125,
                                                mr: 1.25,
                                                fontSize: 18,
                                                verticalAlign: 'middle',
                                            }}
                                        />
                                    </Tooltip>
                                )}

                                {!!data?.bookingId && (
                                    <Typography
                                        sx={{
                                            ...TABLE_CELL_TYPOGRAPHY_STYLES_INVOICED,
                                            fontSize: 14,
                                            fontWeight: 400,
                                            color: 'text.primary',
                                        }}
                                    >
                                        {`BKG-${data.bookingId}`}
                                    </Typography>
                                )}

                                <Typography
                                    sx={{
                                        ...TABLE_CELL_TYPOGRAPHY_STYLES_INVOICED,
                                        fontSize: 12,
                                        fontWeight: 400,
                                        '& span': { fontSize: 14, fontWeight: 600 },
                                    }}
                                >
                                    Week ending:&nbsp;
                                    <span>{weekEnding ? formatUtcDate(weekEnding) : ''}</span>
                                </Typography>

                                <Typography
                                    sx={{
                                        ...TABLE_CELL_TYPOGRAPHY_STYLES_INVOICED,
                                        fontSize: 12,
                                        fontWeight: 400,
                                        lineHeight: '16px',
                                        '& span': { fontSize: 14, fontWeight: 600 },
                                    }}
                                >
                                    Total to: Bill:&nbsp;
                                    <span>${Number(totalToBill).toFixed(2)}</span>
                                </Typography>

                                {!!timesheetId && (
                                    <Button
                                        variant="text"
                                        component={Link}
                                        to={clinicianTimesheetUrl}
                                        target="_blank"
                                        size="small"
                                        startIcon={<LaunchIcon />}
                                        sx={{ p: 0.5, fontSize: 12 }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        Clinician timesheet
                                    </Button>
                                )}

                                {!!processedTimesheetId && (
                                    <Button
                                        variant="text"
                                        component={Link}
                                        to={`/processed-timesheets/${processedTimesheetId}?followToAggregate=true`}
                                        target="_blank"
                                        size="small"
                                        startIcon={<LaunchIcon />}
                                        sx={{ p: 0.5, fontSize: 12 }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        Processed timesheet
                                    </Button>
                                )}
                            </Stack>
                        </Stack>
                    )}

                    {type === 'expense' && (
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ width: '100%' }}
                        >
                            {/* Left: label + booking */}
                            <Stack
                                direction="row"
                                alignItems="center"
                                gap={2}
                                sx={{ pl: CHILD_PADDING / 2 }}
                            >
                                <Typography sx={TABLE_CELL_TYPOGRAPHY_STYLES_INVOICED}>
                                    Expenses
                                </Typography>

                                {!!data?.bookingId && data.isHighPriority && (
                                    <Tooltip title="High Priority" placement="top" arrow>
                                        <FlagIcon
                                            sx={{
                                                color: COLORS.LT_PEPPER125,
                                                mr: 1.25,
                                                fontSize: 18,
                                                verticalAlign: 'middle',
                                            }}
                                        />
                                    </Tooltip>
                                )}

                                {!!data?.bookingId && (
                                    <Typography
                                        sx={{
                                            fontSize: 14,
                                            color: 'text.primary',
                                            fontWeight: 400,
                                        }}
                                    >
                                        {`BKG-${data.bookingId}`}
                                    </Typography>
                                )}
                                {/* Right: total to bill */}
                                <Typography
                                    sx={{
                                        ...TABLE_CELL_TYPOGRAPHY_STYLES_INVOICED,
                                        fontSize: 12,
                                        fontWeight: 400,
                                        lineHeight: '16px',
                                        '& span': { fontSize: 14, fontWeight: 600 },
                                    }}
                                >
                                    Total to bill:&nbsp;
                                    <span>${Number(totalToBill).toFixed(2)}</span>
                                </Typography>
                            </Stack>
                        </Stack>
                    )}
                </Box>
            </Stack>

            {hasSubItems && (
                <Collapse in={isExpanded} id={`subitem-collapse-${itemId}`}>
                    {renderConnector(isExpanded)}

                    {type === 'timesheet' && (
                        <Stack sx={{ px: CHILD_PADDING, mt: '10px' }} gap="30px">
                            {data?.groupedTimesheets?.map((g) => (
                                <CollapsibleDataGridWithConnectorLines
                                    key={`${data?.processedTimesheetId}:${g?.locationId}`}
                                    bookingId={data?.bookingId}
                                    title={g?.locationName}
                                    itemId={g?.locationId}
                                    itemType="location"
                                    columns={timesheetLineItemColumns}
                                    rows={g?.lineItems || []}
                                    hierarchyConnectorVerticalRef={hierarchyConnectorVerticalRef}
                                    lastChildRef={lastChildRef}
                                    isExpanded={isExpanded}
                                    disableSelection
                                />
                            ))}
                        </Stack>
                    )}

                    {type === 'expense' && (
                        <Box sx={{ px: CHILD_PADDING, mt: '10px' }}>
                            <CollapsibleDataGridWithConnectorLines
                                bookingId={data?.bookingId}
                                itemId={data?.expenseId}
                                itemType="expense"
                                columns={expenseColumns}
                                rows={data?.items || []}
                                hierarchyConnectorVerticalRef={hierarchyConnectorVerticalRef}
                                lastChildRef={lastChildRef}
                                isExpanded={isExpanded}
                                disableSelection
                                sx={{ maxWidth: '800px' }}
                            />
                        </Box>
                    )}
                </Collapse>
            )}
        </Box>
    );
};

export default forwardRef(InvoicingItemContainer);
